<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate24h } from '@/utils/dateFormatter'
import { useToast } from '@/composables/useToast'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'Settings',
  },
})

const { t } = useI18n()
const { showToast } = useToast()

// 👉 Data
const fieldTypes = ref<any[]>([])
const saving = ref(false)

// 👉 Dialog states
const isAddDialogVisible = ref(false)
const isEditDialogVisible = ref(false)
const currentField = ref<any>(null)

// 👉 Form data
const fieldForm = ref({
  name: '',
  label: '',
  type: 'text',
  isRequired: false,
  options: [] as string[],
  validation: '',
  isActive: true,
})

// 👉 Search and filter
const searchQuery = ref('')
const selectedType = ref()
const selectedStatus = ref()

// 👉 Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// Update data table options
const updateOptions = (options: any) => {
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// 👉 Status options
const status = computed(() => [
  { title: t('common.active'), value: true },
  { title: t('common.inactive'), value: false },
])

// 👉 Table headers
const headers = computed(() => [
  { title: t('admin.fieldLabel'), key: 'label', sortable: true },
  { title: t('admin.fieldType'), key: 'type', sortable: true },
  { title: t('common.required'), key: 'isRequired', sortable: true },
  { title: t('common.status'), key: 'isActive', sortable: true },
  { title: t('common.createdAt'), key: 'createdAt', sortable: true },
  { title: t('common.actions'), key: 'actions', sortable: false },
])

// 👉 Fetching custom fields
const { data: customFieldsData, execute: loadCustomFields, isFetching: loading } = useApi<any>(createUrl('/custom-fields', {
  query: {
    q: searchQuery,
    type: selectedType,
    status: selectedStatus,
    itemsPerPage,
    page,
    sortBy,
    orderBy,
  },
}))

// 👉 Helper functions
const getFieldTypeLabel = (type: string) => {
  const typeMap: Record<string, string> = {
    text: t('admin.fieldTypeText'),
    number: t('admin.fieldTypeNumber'),
    email: t('admin.fieldTypeEmail'),
    phone: t('admin.fieldTypePhone'),
    date: t('admin.fieldTypeDate'),
    select: t('admin.fieldTypeSelect'),
    multiselect: t('admin.fieldTypeMultiselect'),
  }

  return typeMap[type] || type
}

const getFieldTypeColor = (type: string) => {
  const colorMap: Record<string, string> = {
    text: 'primary',
    number: 'success',
    email: 'info',
    phone: 'warning',
    date: 'secondary',
    select: 'error',
    multiselect: 'error',
  }

  return colorMap[type] || 'primary'
}

// 👉 Computed properties
const customFields = computed(() => customFieldsData.value?.data || [])
const totalItems = computed(() => customFieldsData.value?.total || 0)

const loadFieldTypes = async () => {
  try {
    const { data } = await useApi('/custom-fields/field-types')

    // Transform field types to AppSelect format
    fieldTypes.value = (data.value as any[])?.map((type: any) => ({
      title: getFieldTypeLabel(type.value),
      value: type.value,
    })) || []
  }
  catch (error) {
    console.error('Error loading field types:', error)
  }
}

// 👉 Dialog functions
const openAddDialog = () => {
  fieldForm.value = {
    name: '',
    label: '',
    type: 'text',
    isRequired: false,
    options: [],
    validation: '',
    isActive: true,
  }
  isAddDialogVisible.value = true
}

const openEditDialog = (field: any) => {
  currentField.value = field
  fieldForm.value = {
    name: field.name,
    label: field.label,
    type: field.type,
    isRequired: field.isRequired,
    options: field.options || [],
    validation: field.validation ? JSON.stringify(field.validation, null, 2) : '',
    isActive: field.isActive,
  }
  isEditDialogVisible.value = true
}

const closeDialogs = () => {
  isAddDialogVisible.value = false
  isEditDialogVisible.value = false
  currentField.value = null
}

// 👉 Save field
const saveField = async () => {
  try {
    saving.value = true

    // Prepare form data
    const formData = {
      ...fieldForm.value,
      validation: fieldForm.value.validation ? JSON.parse(fieldForm.value.validation) : null,
    }

    if (isEditDialogVisible.value && currentField.value) {
      // Update field
      await $api(`/custom-fields/${currentField.value.id}`, {
        method: 'PATCH',
        body: formData,
      })
      showToast({
        type: 'success',
        message: t('userFields.fieldUpdatedSuccessfully'),
      })
    }
    else {
      // Create field
      await $api('/custom-fields', {
        method: 'POST',
        body: formData,
      })
      showToast({
        type: 'success',
        message: t('userFields.fieldCreatedSuccessfully'),
      })
    }

    await loadCustomFields()
    closeDialogs()
  }
  catch (error) {
    console.error('Error saving field:', error)
    showToast({
      type: 'error',
      message: t('userFields.error'),
    })
  }
  finally {
    saving.value = false
  }
}

// 👉 Toggle active
const toggleActive = async (field: any) => {
  try {
    await $api(`/custom-fields/${field.id}/toggle-active`, {
      method: 'PATCH',
    })
    showToast({
      type: 'success',
      message: t('userFields.fieldStatusUpdatedSuccessfully'),
    })
    await loadCustomFields()
  }
  catch (error) {
    console.error('Error toggling field:', error)
    showToast({
      type: 'error',
      message: t('userFields.error'),
    })
  }
}

// 👉 Add option
const addOption = () => {
  fieldForm.value.options.push('')
}

// 👉 Remove option
const removeOption = (index: number) => {
  fieldForm.value.options.splice(index, 1)
}

// 👉 Check if field type needs options
const needsOptions = computed(() => {
  return ['select', 'multiselect'].includes(fieldForm.value.type)
})

// 👉 Watch for search and filter changes
watch([searchQuery, selectedType, selectedStatus, page, itemsPerPage, sortBy, orderBy], () => {
  loadCustomFields()
})

// 👉 Load data on mount
onMounted(async () => {
  await loadFieldTypes()
  loadCustomFields()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('admin.userFieldDefined') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('admin.userFieldSubtitle') }}
      </p>
    </div>

    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>{{ t('common.filters') }}</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Search -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppTextField
              v-model="searchQuery"
              :placeholder="t('common.search')"
              prepend-inner-icon="tabler-search"
              clearable
            />
          </VCol>

          <!-- 👉 Select Type -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedType"
              :placeholder="t('common.selectType')"
              :items="fieldTypes"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>

          <!-- 👉 Select Status -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedStatus"
              :placeholder="t('common.selectStatus')"
              :items="status"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VCardText>
        <div class="d-flex justify-space-between align-center mb-6">
          <h5 class="text-h5">
            {{ t('admin.customFields') }}
          </h5>

          <!-- 👉 Add field button -->
          <VBtn
            prepend-icon="tabler-plus"
            @click="openAddDialog"
          >
            {{ t('common.add') }} {{ t('admin.customField') }}
          </VBtn>
        </div>

        <!-- 👉 Data Table -->
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :items="customFields"
          :items-length="totalItems"
          :loading="loading"
          class="text-no-wrap"
          @update:options="updateOptions"
        >
          <!-- 👉 Field Label -->
          <template #item.label="{ item }">
            <div class="d-flex align-center">
              <div>
                <h6 class="text-h6 mb-1">
                  {{ (item as any).label }}
                </h6>
                <span class="text-body-2 text-medium-emphasis">
                  {{ (item as any).name }}
                </span>
              </div>
            </div>
          </template>

          <!-- 👉 Field Type -->
          <template #item.type="{ item }">
            <VChip
              :color="getFieldTypeColor((item as any).type)"
              size="small"
              label
              class="text-capitalize"
            >
              {{ getFieldTypeLabel((item as any).type) }}
            </VChip>
          </template>

          <!-- 👉 Required -->
          <template #item.isRequired="{ item }">
            <VChip
              :color="(item as any).isRequired ? 'error' : 'default'"
              size="small"
              label
            >
              {{ (item as any).isRequired ? t('common.yes') : t('common.no') }}
            </VChip>
          </template>

          <!-- 👉 Status -->
          <template #item.isActive="{ item }">
            <VChip
              :color="(item as any).isActive ? 'success' : 'error'"
              size="small"
              label
              class="text-capitalize"
            >
              {{ (item as any).isActive ? t('common.active') : t('common.inactive') }}
            </VChip>
          </template>

          <!-- 👉 Created At -->
          <template #item.createdAt="{ item }">
            <span class="text-sm">
              {{ formatDate24h((item as any).createdAt) }}
            </span>
          </template>

          <!-- 👉 Actions -->
          <template #item.actions="{ item }">
            <div class="d-flex gap-1">
              <IconBtn @click="openEditDialog(item as any)">
                <VIcon icon="tabler-edit" />
              </IconBtn>

              <IconBtn @click="toggleActive(item as any)">
                <VIcon :icon="(item as any).isActive ? 'tabler-eye-off' : 'tabler-eye'" />
              </IconBtn>
            </div>
          </template>

          <!-- 👉 Bottom slot -->
          <template #bottom>
            <TablePagination
              v-model:page="page"
              :items-per-page="itemsPerPage"
              :total-items="totalItems"
            />
          </template>
        </VDataTableServer>
      </VCardText>
    </VCard>

    <!-- 👉 Add/Edit Dialog -->
    <VDialog
      v-model="isAddDialogVisible"
      max-width="600"
    >
      <VCard>
        <VCardTitle class="text-h5 pa-6 pb-4">
          {{ t('common.add') }} {{ t('admin.customField') }}
        </VCardTitle>

        <VCardText class="pt-0">
          <VRow>
            <!-- 👉 Field Name -->
            <VCol cols="12">
              <AppTextField
                v-model="fieldForm.name"
                :label="t('admin.fieldName')"
                :placeholder="t('admin.enterFieldName')"
                required
              />
            </VCol>

            <!-- 👉 Field Label -->
            <VCol cols="12">
              <AppTextField
                v-model="fieldForm.label"
                :label="t('admin.fieldLabel')"
                :placeholder="t('admin.enterFieldLabel')"
                required
              />
            </VCol>

            <!-- 👉 Field Type -->
            <VCol cols="12">
              <AppSelect
                v-model="fieldForm.type"
                :label="t('admin.fieldType')"
                :items="fieldTypes"
                required
              />
            </VCol>

            <!-- 👉 Required -->
            <VCol cols="12">
              <VCheckbox
                v-model="fieldForm.isRequired"
                :label="t('common.required')"
              />
            </VCol>

            <!-- 👉 Options (for select/multiselect) -->
            <VCol
              v-if="needsOptions"
              cols="12"
            >
              <VLabel class="text-body-2 mb-2">
                {{ t('common.options') }}
              </VLabel>
              <div
                v-for="(option, index) in fieldForm.options"
                :key="index"
                class="d-flex align-center mb-2"
              >
                <AppTextField
                  v-model="fieldForm.options[index]"
                  :placeholder="t('admin.enterOption')"
                  class="me-2"
                />
                <VBtn
                  icon="tabler-x"
                  size="small"
                  variant="text"
                  @click="removeOption(index)"
                />
              </div>
              <VBtn
                variant="outlined"
                size="small"
                prepend-icon="tabler-plus"
                @click="addOption"
              >
                {{ t('common.addOption') }}
              </VBtn>
            </VCol>

            <!-- 👉 Validation Rules -->
            <VCol cols="12">
              <AppTextarea
                v-model="fieldForm.validation"
                :label="t('admin.fieldTypeValidation')"
                :placeholder="t('admin.fieldTypePlaceholder')"
                rows="3"
              />
            </VCol>

            <!-- 👉 Active -->
            <VCol cols="12">
              <VCheckbox
                v-model="fieldForm.isActive"
                :label="t('common.active')"
              />
            </VCol>
          </VRow>
        </VCardText>

        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="closeDialogs"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            :loading="saving"
            @click="saveField"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 👉 Edit Dialog -->
    <VDialog
      v-model="isEditDialogVisible"
      max-width="600"
    >
      <VCard>
        <VCardTitle class="text-h5 pa-6 pb-4">
          {{ t('common.edit') }} {{ t('admin.customField') }}
        </VCardTitle>

        <VCardText class="pt-0">
          <VRow>
            <!-- 👉 Field Name -->
            <VCol cols="12">
              <AppTextField
                v-model="fieldForm.name"
                :label="t('admin.fieldName')"
                :placeholder="t('admin.enterFieldName')"
                required
              />
            </VCol>

            <!-- 👉 Field Label -->
            <VCol cols="12">
              <AppTextField
                v-model="fieldForm.label"
                :label="t('admin.fieldLabel')"
                :placeholder="t('admin.enterFieldLabel')"
                required
              />
            </VCol>

            <!-- 👉 Field Type -->
            <VCol cols="12">
              <AppSelect
                v-model="fieldForm.type"
                :label="t('admin.fieldType')"
                :items="fieldTypes"
                required
              />
            </VCol>

            <!-- 👉 Required -->
            <VCol cols="12">
              <VCheckbox
                v-model="fieldForm.isRequired"
                :label="t('common.required')"
              />
            </VCol>

            <!-- 👉 Options (for select/multiselect) -->
            <VCol
              v-if="needsOptions"
              cols="12"
            >
              <VLabel class="text-body-2 mb-2">
                {{ t('common.options') }}
              </VLabel>
              <div
                v-for="(option, index) in fieldForm.options"
                :key="index"
                class="d-flex align-center mb-2"
              >
                <AppTextField
                  v-model="fieldForm.options[index]"
                  :placeholder="t('admin.enterOption')"
                  class="me-2"
                />
                <VBtn
                  icon="tabler-x"
                  size="small"
                  variant="text"
                  @click="removeOption(index)"
                />
              </div>
              <VBtn
                variant="outlined"
                size="small"
                prepend-icon="tabler-plus"
                @click="addOption"
              >
                {{ t('common.addOption') }}
              </VBtn>
            </VCol>

            <!-- 👉 Validation Rules -->
            <VCol cols="12">
              <AppTextarea
                v-model="fieldForm.validation"
                :label="t('admin.fieldTypeValidation')"
                :placeholder="t('admin.fieldTypePlaceholder')"
                rows="3"
              />
            </VCol>

            <!-- 👉 Active -->
            <VCol cols="12">
              <VCheckbox
                v-model="fieldForm.isActive"
                :label="t('common.active')"
              />
            </VCol>
          </VRow>
        </VCardText>

        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="outlined"
            @click="closeDialogs"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            :loading="saving"
            @click="saveField"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
